import {
  Button,
  EButtonColorVariant,
  EIconButtonSize,
  EIconButtonVariant,
  ETagVariant,
  GeneralCaretLeftIcon,
  GeneralShareInitialIcon,
  IconButton,
  Tag,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import NextLink from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ShareModal from '@/components/ui/ShareModal/ShareModal';
import { PHOTOS } from '@/constants/urls';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';

type TActionsBarProps = {
  title: string;
  description: string;
  backUrl: string;
  photoCount: number;
  isClaimed?: boolean;
};

export const ActionsBar = ({
  title,
  description,
  backUrl,
  photoCount,
  isClaimed,
}: TActionsBarProps) => {
  const { isMobile } = useBreakpoint();

  const [isShareOpen, setIsShareOpen] = useState(false);
  const intl = useIntl();

  const handleShare = () => {
    if (window?.navigator && window?.navigator.share) {
      window.navigator.share({
        title,
        text: description,
        url: window.location.href,
      });
    } else {
      setIsShareOpen(!isShareOpen);
    }
  };

  return (
    <>
      {(isMobile && (
        <>
          <MobileActionsBar backUrl={backUrl} handleShare={handleShare} />
          <div className="absolute bottom-0 w-full px-4 mb-4 text-end">
            <Tag
              label={intl.formatMessage(
                { defaultMessage: '1 of {photoCount}', id: 'W2HUSG' },
                { photoCount },
              )}
              variant={ETagVariant.Dark}
            />
          </div>
        </>
      )) || (
        <DesktopActionsBar
          handleShare={handleShare}
          photoCount={photoCount}
          isClaimed={isClaimed}
        />
      )}

      <ShareModal
        title={title}
        description={description}
        isOpen={isShareOpen}
        onClose={handleShare}
      />
    </>
  );
};

type TMobileActionsBarProps = {
  backUrl: string;
  handleShare: () => void;
};

const MobileActionsBar = ({ backUrl, handleShare }: TMobileActionsBarProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (!wrapperRef.current) return;

      setIsFixed(wrapperRef.current.getBoundingClientRect().top < 0);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const iconButtonsVariant = isFixed ? EIconButtonVariant.Ghost : EIconButtonVariant.Primary;

  return (
    <div
      ref={wrapperRef}
      className="absolute top-0 left-0 w-full overflow-hidden pointer-events-none">
      <div className={clsx({ 'fixed top-0 left-0 w-full z-50': isFixed })}>
        <div
          className={clsx(
            'absolute inset-0 border-b border-gray-200 pointer-events-auto bg-primary-contrast transition-transform duration-200 ease-in-out',
            isFixed ? 'translate-y-0' : '-translate-y-full',
          )}
        />

        <div className="relative flex justify-between p-4 pointer-events-none [&>*]:pointer-events-auto">
          <NextLink href={backUrl} passHref legacyBehavior>
            <IconButton
              icon={GeneralCaretLeftIcon}
              size={EIconButtonSize.Small}
              variant={iconButtonsVariant}
            />
          </NextLink>

          <IconButton
            onClick={handleShare}
            icon={GeneralShareInitialIcon}
            size={EIconButtonSize.Small}
            variant={iconButtonsVariant}
          />
        </div>
      </div>
    </div>
  );
};

type TDesktopActionsBarProps = {
  handleShare: () => void;
  photoCount: number;
  isClaimed?: boolean;
};

const DesktopActionsBar = ({ handleShare, photoCount, isClaimed }: TDesktopActionsBarProps) => {
  const { query } = useRouter();
  const params = new URLSearchParams(query as Record<string, string>).toString();

  return (
    <div className="absolute left-4 bottom-4">
      {isClaimed && (
        <Button
          className="mr-4"
          onDark
          variant={EButtonColorVariant.Tertiary}
          href={`/campground-rental/${query?.location}/${query?.slug}${PHOTOS}?${params}`}
          label={
            <FormattedMessage
              defaultMessage="View {photoCount, plural, one {# photo} other {# photos}}"
              id="7bdc8w"
              values={{ photoCount }}
            />
          }
        />
      )}

      <Button
        onClick={handleShare}
        label={
          <span className="flex items-center">
            <GeneralShareInitialIcon className="mr-2" />
            <FormattedMessage defaultMessage="Share" id="OKhRC6" />
          </span>
        }
        variant={EButtonColorVariant.Tertiary}
        onDark
      />
    </div>
  );
};
